import React, { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Activity,
  Camera,
  Download,
  Loader2,
  TrendingDown,
} from "lucide-react";
import { SaddleCloth } from "./SaddleCloth";
import {
  compareDdSnapshots,
  fmtDollars,
  fmtPct,
  DEFAULT_DD_TAKEOUT,
} from "../lib/lateMoney";
import { useAutoRefresh } from "../hooks/useAutoRefresh";

const monoStyle = {
  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
};

const SnapshotCard = ({
  label,
  snapshot,
  setSnapshot,
  trackSlug,
  apiBase,
  busyKey,
  setBusy,
}) => {
  const inputRef = useRef(null);
  const matrix = snapshot?.matrix || {};
  const legs = Object.keys(matrix).length;

  const parseImage = useCallback(
    async (dataUrl) => {
      setBusy(busyKey);
      try {
        const resp = await fetch(`${apiBase}/scan-dd-matrix`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ image_base64: dataUrl }),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err?.detail || `HTTP ${resp.status}`);
        }
        const data = await resp.json();
        setSnapshot({
          pool: data.pool ?? snapshot?.pool ?? null,
          matrix: data.matrix,
          source: "screenshot",
          ts: Date.now(),
        });
        toast.success(
          `${label} snapshot parsed — ${
            Object.keys(data.matrix || {}).length
          } leg-1 posts${data.pool ? ` · pool $${data.pool.toLocaleString()}` : ""}`
        );
      } catch (err) {
        toast.error(`Scan failed: ${err.message || err}`);
      } finally {
        setBusy(null);
      }
    },
    [apiBase, busyKey, label, setBusy, setSnapshot, snapshot]
  );

  const onFileChange = async (file) => {
    if (!file) return;
    const b64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    parseImage(b64);
  };

  const captureLive = useCallback(async () => {
    if (!trackSlug) {
      toast.error("Select a track in the loader above first.");
      return;
    }
    setBusy(busyKey);
    try {
      const resp = await fetch(
        `${apiBase}/fetch-dd-probables?track=${encodeURIComponent(trackSlug)}`
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      // Convert Cybertote string matrix → numeric, dash → null.
      const numMatrix = {};
      for (const [k1, row] of Object.entries(data.matrix || {})) {
        numMatrix[k1] = {};
        for (const [k2, raw] of Object.entries(row)) {
          const s = String(raw).trim();
          const n = Number(s);
          numMatrix[k1][k2] = s === "" || s === "--" || s === "-" || !Number.isFinite(n) || n <= 0 ? null : n;
        }
      }
      setSnapshot({
        pool: data.pool ?? null,
        matrix: numMatrix,
        source: "cybertote",
        ts: Date.now(),
      });
      if (!Object.keys(numMatrix).length) {
        toast.warning(data.note || "No probables available yet.");
      } else {
        toast.success(
          `${label} snapshot captured${
            data.pool ? ` · pool $${Number(data.pool).toLocaleString()}` : ""
          }`
        );
      }
    } catch (err) {
      toast.error(`Capture failed: ${err.message || err}`);
    } finally {
      setBusy(null);
    }
  }, [apiBase, busyKey, label, setBusy, setSnapshot, trackSlug]);

  const setPool = (v) => {
    const n = Number(String(v).replace(/[^\d.]/g, ""));
    setSnapshot({
      ...(snapshot || { matrix: {} }),
      pool: Number.isFinite(n) && n > 0 ? n : null,
    });
  };

  const clear = () => setSnapshot(null);

  const busy = busyKey === busyKey && false; // overridden by parent

  return (
    <div
      className="p-4 rounded border border-stone-800 bg-[#0d0d0d] flex flex-col gap-3"
      data-testid={`late-money-${busyKey}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div
          className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#facc15]"
          style={monoStyle}
        >
          {label}
        </div>
        {snapshot && (
          <button
            onClick={clear}
            className="text-[10px] uppercase tracking-[0.18em] text-stone-500 hover:text-red-400"
            style={monoStyle}
          >
            Clear
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-1.5 px-3 h-8 rounded border border-stone-700 text-stone-200 text-[11px] uppercase tracking-[0.18em] hover:border-[#f5ecd6]"
          style={monoStyle}
          data-testid={`${busyKey}-upload-btn`}
        >
          <Camera size={12} /> Screenshot
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) onFileChange(f);
          }}
        />
        <button
          onClick={captureLive}
          disabled={!trackSlug}
          className="inline-flex items-center gap-1.5 px-3 h-8 rounded border border-[#365314] bg-[#1a2e0a] text-[#a3e635] text-[11px] uppercase tracking-[0.18em] font-bold hover:bg-[#84cc16] hover:text-[#0a0a0a] disabled:opacity-40"
          style={monoStyle}
          data-testid={`${busyKey}-capture-btn`}
          title={trackSlug ? `Pull live from Cybertote: ${trackSlug}` : "Select a track above first"}
        >
          <Download size={12} /> Capture Now
        </button>
      </div>

      <div className="flex items-center gap-2 text-[11px]" style={monoStyle}>
        <span className="text-stone-500 uppercase tracking-[0.2em]">Pool $</span>
        <input
          value={snapshot?.pool ?? ""}
          onChange={(e) => setPool(e.target.value)}
          placeholder="19637.01"
          className="px-2 h-7 w-32 rounded bg-[#0a0a0a] text-stone-100 text-[11px] border border-stone-700 focus:border-[#84cc16] focus:outline-none"
          style={monoStyle}
          data-testid={`${busyKey}-pool`}
        />
        <span className="text-stone-500 uppercase tracking-[0.2em] ml-2">
          Posts: {legs}
        </span>
        {snapshot?.source && (
          <span className="text-stone-500 uppercase tracking-[0.18em] text-[10px] ml-auto">
            via {snapshot.source}
          </span>
        )}
      </div>
    </div>
  );
};

export const LateMoneyDetector = ({ apiBase, trackSlug, trackName, race }) => {
  const [open, setOpen] = useState(false);
  const [early, setEarly] = useState(null);
  const [finalSnap, setFinalSnap] = useState(null);
  const [busy, setBusy] = useState(null);
  const [auto, setAuto] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Silent 30s refresh for the "Final" snapshot — keeps the baseline
  // (Early) frozen while the Final column keeps rolling forward so the
  // user can watch late money accrue in real time.
  const refreshFinal = useCallback(async () => {
    if (!trackSlug) return;
    try {
      const resp = await fetch(
        `${apiBase}/fetch-dd-probables?track=${encodeURIComponent(trackSlug)}`
      );
      if (!resp.ok) return;
      const data = await resp.json();
      const numMatrix = {};
      for (const [k1, row] of Object.entries(data.matrix || {})) {
        numMatrix[k1] = {};
        for (const [k2, raw] of Object.entries(row)) {
          const s = String(raw).trim();
          const n = Number(s);
          numMatrix[k1][k2] =
            s === "" || s === "--" || s === "-" || !Number.isFinite(n) || n <= 0
              ? null
              : n;
        }
      }
      if (!Object.keys(numMatrix).length) return;
      setFinalSnap({
        pool: data.pool ?? null,
        matrix: numMatrix,
        source: "cybertote",
        ts: Date.now(),
      });
      setLastRefresh(Date.now());
    } catch {
      /* silent */
    }
  }, [apiBase, trackSlug]);

  useAutoRefresh(refreshFinal, {
    enabled: auto && open && !!trackSlug,
    intervalMs: 30000,
  });

  const combos = compareDdSnapshots(early, finalSnap);
  const poolGrowth =
    early?.pool && finalSnap?.pool
      ? finalSnap.pool / early.pool - 1
      : null;

  return (
    <section
      className="rounded-lg border border-stone-800 bg-[#0e0e0e] mt-5 overflow-hidden"
      data-testid="late-money-detector"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-3 border-b border-stone-800 hover:bg-[#141414]"
        data-testid="late-money-toggle"
      >
        <div className="flex items-center gap-2 text-[#f5ecd6]">
          <Activity size={16} className="text-[#84cc16]" />
          <span
            className="text-sm uppercase tracking-[0.2em]"
            style={monoStyle}
          >
            Late Money Detector
          </span>
          {trackName && (
            <span
              className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#1a2e05] text-[#a3e635] border border-[#365314] font-bold tracking-[0.14em]"
              style={monoStyle}
              data-testid="late-money-target"
            >
              {trackName}{race ? ` · R${race}` : ""}
            </span>
          )}
          {combos.length > 0 && (
            <span
              className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#84cc16] text-[#0a0a0a] font-bold tracking-[0.14em]"
              style={monoStyle}
            >
              {combos.length} combos
            </span>
          )}
        </div>
        <span
          className="text-[10px] uppercase tracking-[0.2em] text-stone-400"
          style={monoStyle}
        >
          {open ? "Hide ▲" : "Show ▼"}
        </span>
      </button>

      {open && (
        <div className="p-4 space-y-4">
          <p
            className="text-[11px] text-stone-400 leading-5"
            style={monoStyle}
          >
            Capture the DD probables matrix twice — once around 1 minute to
            post, again after the pool closes — and this panel ranks the
            combos that attracted the most late (CAW) money.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <label
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-stone-300 cursor-pointer select-none"
              style={monoStyle}
              data-testid="late-money-auto-toggle"
              title={
                !trackSlug
                  ? "Select a track above first"
                  : "Re-captures the Final snapshot every 30s. Early stays frozen as your baseline. Pauses while this tab is hidden."
              }
            >
              <input
                type="checkbox"
                checked={auto}
                onChange={(e) => setAuto(e.target.checked)}
                disabled={!trackSlug}
                className="accent-[#84cc16] w-4 h-4 disabled:opacity-40"
              />
              Auto-refresh Final every 30s
              {auto && (
                <span className="inline-flex h-2 w-2 rounded-full bg-[#84cc16] animate-pulse" />
              )}
            </label>
            {lastRefresh && (
              <span
                className="text-[10px] uppercase tracking-[0.2em] text-stone-500"
                style={monoStyle}
                data-testid="late-money-last-refresh"
              >
                Final updated{" "}
                {new Date(lastRefresh).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </span>
            )}
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <SnapshotCard
              label="Early · ~1 min to post"
              snapshot={early}
              setSnapshot={setEarly}
              trackSlug={trackSlug}
              apiBase={apiBase}
              busyKey="early"
              setBusy={setBusy}
            />
            <SnapshotCard
              label="Final · pool closed"
              snapshot={finalSnap}
              setSnapshot={setFinalSnap}
              trackSlug={trackSlug}
              apiBase={apiBase}
              busyKey="final"
              setBusy={setBusy}
            />
          </div>

          {busy && (
            <div
              className="text-[11px] uppercase tracking-[0.2em] text-[#facc15] flex items-center gap-2"
              style={monoStyle}
              data-testid="late-money-busy"
            >
              <Loader2 size={12} className="animate-spin" /> Parsing{" "}
              {busy === "early" ? "early" : "final"} snapshot…
            </div>
          )}

          {combos.length === 0 && !busy && (
            <div
              className="text-[11px] text-stone-500 italic px-2"
              style={monoStyle}
            >
              Add both snapshots to see the ranking.
            </div>
          )}

          {combos.length > 0 && (
            <div
              data-testid="late-money-results"
              className="rounded border border-stone-800 overflow-hidden"
            >
              <div
                className="px-3 py-2 border-b border-stone-800 bg-[#141414] flex items-center justify-between flex-wrap gap-2"
                style={monoStyle}
              >
                <div className="text-[10px] uppercase tracking-[0.22em] text-stone-400 flex items-center gap-2">
                  <TrendingDown size={12} className="text-[#84cc16]" />
                  Late-money ranking · willpay drop first
                </div>
                {poolGrowth != null && (
                  <div className="text-[10px] uppercase tracking-[0.2em] text-stone-400">
                    Pool grew{" "}
                    <span className="text-[#f5ecd6] font-bold">
                      {fmtPct(poolGrowth)}
                    </span>{" "}
                    (${Number(early.pool).toLocaleString()} → $
                    {Number(finalSnap.pool).toLocaleString()})
                  </div>
                )}
              </div>
              <table
                className="w-full text-[11px]"
                style={monoStyle}
                data-testid="late-money-table"
              >
                <thead className="bg-[#0a0a0a] text-stone-500 uppercase tracking-[0.18em] text-[10px]">
                  <tr>
                    <th className="text-left px-3 py-2">#</th>
                    <th className="text-left px-3 py-2">Leg 1 → Leg 2</th>
                    <th className="text-right px-3 py-2">Early $1</th>
                    <th className="text-right px-3 py-2">Final $1</th>
                    <th className="text-right px-3 py-2">Drop %</th>
                    <th className="text-right px-3 py-2">$ Delta</th>
                    <th className="text-right px-3 py-2">vs Proportional</th>
                  </tr>
                </thead>
                <tbody>
                  {combos.map((c, i) => {
                    const top4 = i < 4;
                    const dropColor =
                      c.dropPct > 0
                        ? "text-[#84cc16]"
                        : c.dropPct < 0
                        ? "text-[#f87171]"
                        : "text-stone-400";
                    return (
                      <tr
                        key={`${c.leg1}-${c.leg2}`}
                        data-testid={`late-row-${c.leg1}-${c.leg2}`}
                        className={`border-t border-stone-800 ${
                          top4 ? "bg-[#1a1f0a]" : ""
                        }`}
                      >
                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                              top4
                                ? "bg-[#84cc16] text-[#0a0a0a]"
                                : "bg-stone-800 text-stone-400"
                            }`}
                          >
                            {i + 1}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <SaddleCloth n={c.leg1} />
                            <span className="text-stone-500">→</span>
                            <SaddleCloth n={c.leg2} />
                          </div>
                        </td>
                        <td className="text-right px-3 py-2 text-stone-300">
                          ${c.earlyWillpay.toFixed(0)}
                        </td>
                        <td className="text-right px-3 py-2 text-stone-300">
                          ${c.finalWillpay.toFixed(0)}
                        </td>
                        <td
                          className={`text-right px-3 py-2 font-bold ${dropColor}`}
                        >
                          {fmtPct(c.dropPct)}
                        </td>
                        <td
                          className={`text-right px-3 py-2 ${
                            c.dollarDelta > 0
                              ? "text-[#84cc16]"
                              : c.dollarDelta < 0
                              ? "text-[#f87171]"
                              : "text-stone-400"
                          }`}
                        >
                          {fmtDollars(c.dollarDelta)}
                        </td>
                        <td
                          className={`text-right px-3 py-2 ${
                            c.deltaVsProportional > 0
                              ? "text-[#84cc16]"
                              : c.deltaVsProportional < 0
                              ? "text-[#f87171]"
                              : "text-stone-400"
                          }`}
                        >
                          {fmtDollars(c.deltaVsProportional)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div
                className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-stone-500 bg-[#0a0a0a] border-t border-stone-800"
                style={monoStyle}
              >
                $ delta assumes {Math.round(DEFAULT_DD_TAKEOUT * 100)}% takeout. Top 4 highlighted
                lime show the strongest late action.
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
